import type { ApiCartela, ApiDraw, ApiEvent, ApiPrize, Cartela, Draw, DrawEvent, Lead, Prize } from '../types';
import { commit, getState } from './store';
import { eventsApi } from '../../features/events/events.api';
import { prizesApi } from '../../features/prizes/prizes.api';
import { missionsApi } from '../../features/missions/missions.api';
import { playersApi } from '../../features/players/players.api';

/**
 * Sincroniza o estado local do estúdio com a API.
 *
 * Sorteios, prêmios, missões, jogadores, cartelas e resultados vivem na API. O estado local
 * segue sendo o cache que as features leem, então aqui a API é traduzida para a forma que o
 * estúdio conhece.
 */
export async function hydrateFromApi(): Promise<void> {
  const [events, missions, apiCartelas] = await Promise.all([
    eventsApi.list(),
    missionsApi.list(),
    playersApi.cartelas({ limit: 5000 }),
  ]);
  const prizeLists = await Promise.all(events.map(e => prizesApi.listByEvent(e.id)));

  const s = getState();
  s.events = events.map(toLocalEvent);
  s.prizes = prizeLists.flat().map(toLocalPrize);
  s.missions = missions;
  s.leads = leadsFrom(apiCartelas);
  s.cartelas = apiCartelas.map(toLocalCartela);

  // resultados vivem no servidor. Sorteios feitos no estúdio antes desta versão (só locais) são
  // enviados agora, uma vez; depois a lista local passa a ser o espelho da API.
  const serverDraws = (await Promise.all(events.map(e => eventsApi.draws(e.id).catch(() => [] as ApiDraw[])))).flat();
  const known = new Set(serverDraws.map(d => d.prizeId));
  const prizeIds = new Set(s.prizes.map(p => p.id));
  const pending = s.draws.filter(d => d.eventId && prizeIds.has(d.prizeId) && !known.has(d.prizeId));
  for (const d of pending) {
    try {
      const saved = await eventsApi.recordDraw(d.eventId!, { prizeId: d.prizeId, cartelaId: d.cartelaId, number: d.number, seed: d.seed, commitHash: d.hash, poolSize: d.poolSize });
      serverDraws.push(saved);
    } catch { /* fica local e tenta de novo na próxima sincronização */ }
  }
  const sent = new Set(serverDraws.map(d => d.prizeId));
  s.draws = [
    ...serverDraws.map(toLocalDraw),
    ...s.draws.filter(d => prizeIds.has(d.prizeId) && !sent.has(d.prizeId)), // ainda não enviados
  ];
  const drawn = new Set(s.draws.map(d => d.prizeId));
  s.prizes.forEach(p => { if (drawn.has(p.id)) p.status = 'drawn'; });

  if (!s.events.some(e => e.id === s.ui.activeEventId)) s.ui.activeEventId = s.events[0]?.id ?? null;
  commit();
}

/** Formato local de data: 'AAAA-MM-DD HH:mm' no fuso do navegador. */
function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function toLocalEvent(e: ApiEvent): DrawEvent {
  return {
    id: e.id,
    name: e.name,
    at: formatWhen(e.scheduledAt),
    tier: e.tier === 'MENSUAL' ? 'mensual' : 'semanal',
    status: e.status.toLowerCase() as DrawEvent['status'],
    liveUrl: e.liveUrl,
    hostName: e.hosts[0]?.name ?? null,
    hostId: e.hosts[0]?.id ?? null,
    hostHandle: e.hosts[0]?.handle ?? null,
    poolCartelas: e.poolCartelas ?? null,
    poolPlayers: e.poolPlayers ?? null,
  };
}

/** Jogadores derivados das cartelas (o palco mostra nome e número; não precisa da lista completa). */
function leadsFrom(cartelas: ApiCartela[]): Lead[] {
  const byId = new Map<string, Lead>();
  for (const c of cartelas) {
    if (!byId.has(c.player.id)) {
      byId.set(c.player.id, { id: c.player.id, name: c.player.name, externalId: c.player.externalId, telegram: '', createdAt: Date.parse(c.createdAt) });
    }
  }
  return [...byId.values()];
}

export function toLocalCartela(c: ApiCartela): Cartela {
  return {
    id: c.id, number: c.number, leadId: c.player.id, missionId: c.mission?.id ?? null,
    createdAt: Date.parse(c.createdAt),
    status: c.status === 'ACTIVE' ? 'active' : c.status === 'WON' ? 'won' : 'expired',
    weeklyEventId: c.weeklyEventId,
  };
}

export function toLocalPrize(p: ApiPrize): Prize {
  return {
    id: p.id, emoji: p.emoji, name: p.name, value: p.value, eventId: p.eventId,
    status: p.status === 'DRAWN' ? 'drawn' : p.status === 'CANCELLED' ? 'cancelled' : 'pending',
  };
}

export function toLocalDraw(d: ApiDraw): Draw {
  return {
    id: d.id, at: Date.parse(d.createdAt), eventId: d.eventId, prizeId: d.prizeId, cartelaId: d.cartelaId,
    leadId: d.playerId, number: d.number, seed: d.seed, hash: d.commitHash, poolSize: d.poolSize,
  };
}
